import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import Switch from "../../../components/common/Switch";
import Button from "../../../components/common/button";
import AddAlarmMusicModal from "../../../components/tv-app/alarm/add-alarm-music-modal";
import DeleteAndroidHome from "../../../components/tv-app/delete-android-home";
import EditTableRowName from "../../../components/tv-app/edit-name";
import DiscardSaveButtons from "../../../components/tv-app/discard-save-buttons";
import {
  ICategoryComponentProps,
  ISounds,
  ITableColumn,
} from "../../../utils/types";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { DndProvider } from "react-dnd";
import useUpdateAndroidTVHomeOrder from "../../../hooks/use-update-android-home-order";
import { HTML5Backend } from "react-dnd-html5-backend";
import DraggableRow from "../../../components/common/DragRow";
import { DragSvg } from "../../../icons";
import { setDragOnlyInStaging } from "../../../utils/helpers";
import { useGetUserPermission } from "../../../context/AuthStaffUserContext";

interface IFormData {
  showMusic: boolean;
  soundsData: ISounds[];
}

const Music = ({ data, loading, onSave }: ICategoryComponentProps) => {
  const getInitialFormData = () => {
    return {
      showMusic: !!data?.musicSettings?.show,
      soundsData: data?.sounds.sort((a, b) => a.orderKey - b?.orderKey),
    };
  };

  const [InitialFormData, setInitialFormData] = useState<IFormData>(
    getInitialFormData()
  );

  const methods = useForm<IFormData>({ defaultValues: InitialFormData });

  const { reset, handleSubmit } = methods;

  const {
    fields: musicFields,
    append: appendMusicField,
    remove: removeMusicField,
  } = useFieldArray({
    name: "soundsData",
    control: methods.control,
    keyName: "customId",
  });

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const categoryName = data?.categoryName || "";
  const [musicData, setMusicData] = useState(() => ({
    categoryName,
    items:
      [...(musicFields as ISounds[])]?.sort(
        (a, b) => a.orderKey - b.orderKey
      ) ?? [], // Use subData directly or default to an empty array
  }));

  const { mutate: updateAndroidTVHomeOrder } = useUpdateAndroidTVHomeOrder();

  useEffect(() => {
    // Sort and update alarmData.items whenever alarmFields changes
    const sorted = [...musicFields].sort((a, b) => a.orderKey - b.orderKey);
    setMusicData((prev) => ({
      ...prev,
      items: sorted,
    }));
  }, [musicFields]);

  useEffect(() => {
    // setSoundsData(JSON.parse(JSON.stringify(data?.sounds)));
    if (musicFields.length < data.sounds.length) {
      const addedElement = data.sounds[data.sounds.length - 1];
      appendMusicField({ ...addedElement });
      setInitialFormData((pre) => ({
        ...pre,
        data: [...pre.soundsData, { ...addedElement }],
      }));
    }
  }, [data.sounds]);

  const getTableDataIndex = (id: string) => {
    return musicFields.findIndex((item) => item.id === id);
  };

  const onDropComplete = () => {
    const updatedItems = [...musicData.items].map((item, index) => ({
      ...item,
      orderKey: index + 1,
    }));

    setMusicData((prev) => ({
      ...prev,
      items: updatedItems.sort((a, b) => a?.orderKey - b?.orderKey),
    }));

    const updatedDataWithOrderKey = updatedItems.map((item) => ({
      id: item.id,
      orderKey: item.orderKey,
    }));
    updateAndroidTVHomeOrder({ categoryName, data: updatedDataWithOrderKey });
  };

  const onSubmit = (submitData: IFormData) => {
    const orderKeyMap = new Map(
      musicData.items.map((item) => [item.id, item.orderKey])
    );

    const mergedData = submitData.soundsData.map((item) => ({
      ...item,
      orderKey: orderKeyMap.get(item.id) ?? item.orderKey,
    }));
    onSave({
      categoryName: data?.categoryName,
      data: {
        ...data,
        musicSettings: {
          ...(data?.musicSettings || {}),
          show: submitData.showMusic,
          subData: mergedData.sort((a, b) => a?.orderKey - b?.orderKey),
        },
        sounds: mergedData.sort((a, b) => a?.orderKey - b?.orderKey),
      },
    });
  };

  const handleSwitchChange = (rowId: string) => {
    const updatedItems = musicData.items
      .sort((a, b) => a?.orderKey - b?.orderKey)
      .map((item, index) => {
        const isSelected = item.id === rowId;

        methods.setValue(`soundsData.${index}.isSelected`, isSelected);

        return {
          ...item,
          isSelected,
        };
      });

    methods.trigger();

    setMusicData({ ...musicData, items: updatedItems });
  };

  const { write: isEditPermission } = useGetUserPermission("tv-app");

  const tableColumns: ITableColumn<ISounds>[] = setDragOnlyInStaging<
    ITableColumn<ISounds>
  >([
    {
      name: "",
      cell: (Row, index) => (
        <DraggableRow
          index={index}
          moveRow={moveRow}
          onDropComplete={onDropComplete}
        >
          <DragSvg
            height={24}
            width={24}
            className="h-6 w-6 cursor-grab flex-none"
          />
        </DraggableRow>
      ),
      sortable: true,
      width: "10%",
    },
    {
      name: "Name",
      cell: (Row) => (
        <EditTableRowName
          name={`soundsData.${getTableDataIndex(Row.id)}.name`}
          disabled={!isEditPermission}
        />
      ),
      sortable: true,
      width: "40%",
    },
    {
      name: "Default",
      cell: (Row) => (
        <Switch
          name={`soundsData.${getTableDataIndex(Row.id)}.isSelected`}
          onCheck={() => handleSwitchChange(Row.id)}
        />
      ),
      sortable: true,
      center: true,
    },
    {
      name: "Hide/Show",
      cell: (Row) => (
        <Switch name={`soundsData.${getTableDataIndex(Row.id)}.selected`} />
      ),
      sortable: true,
      center: true,
      width: "20%",
    },
    {
      name: "Status",
      cell: (Row) => (
        <DeleteAndroidHome
          categoryName={data?.categoryName}
          deleteId={Row.id}
          disabled={!isEditPermission}
          removeField={() => {
            setInitialFormData((pre) => ({
              ...pre,
              data: pre.soundsData.filter((item) => item.id !== Row.id),
            }));
            removeMusicField(getTableDataIndex(Row.id));
            setMusicData({
              ...musicData,
              items: musicData.items.filter((item) => item.id !== Row.id),
            });
          }}
        />
      ),
      sortable: true,
      center: true,
      width: "20%",
    },
  ]);

  const moveRow = (fromIndex: number, toIndex: number) => {
    if (!musicData?.items) return;
    const updatedData = [...musicData.items];
    const [movedItem] = updatedData.splice(fromIndex, 1);
    updatedData.splice(toIndex, 0, movedItem);
    setMusicData({
      ...musicData,
      items: updatedData,
    });
  };

  return (
    <FormProvider {...methods}>
      <div>
        <h3 className="text-[22px] font-normal text-[#1E2F65]">Music</h3>
        <div className="">
          {/* <Switch name="showMusic" label="Music (Hide/Show)" /> */}
        </div>
        <div className="flex items-center justify-between w-full mt-4">
          <h4 className="text-base leading-[39px] font-normal text-[#1E2F65]">
            Music List
          </h4>
          <Button
            variant="primary"
            className="w-21"
            onClick={openModal}
            disabled={!isEditPermission}
          >
            Add
          </Button>
        </div>
        {/* <div className="h-full max-h-[calc(100vh-440px)] overflow-auto mt-[17px]"> */}
        <DndProvider backend={HTML5Backend}>
          <DataTable
            className="border-t mt-6"
            data={musicData?.items || []}
            columns={tableColumns}
            striped={true}
            keyField="customId"
            persistTableHead
            pagination={musicFields.length > 10}
            customStyles={{
              headRow: { style: { paddingLeft: "36px" } },
              rows: { style: { paddingLeft: "36px" } },
            }}
          />
        </DndProvider>
        {/* </div> */}

        <DiscardSaveButtons
          onDiscard={() => {
            reset(getInitialFormData);
          }}
          onSave={handleSubmit(onSubmit)}
          loading={loading}
          className="mt-10"
        />

        <AddAlarmMusicModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          categoryName={data.categoryName}
          modalTitle="Add New Music"
        />
      </div>
    </FormProvider>
  );
};

export default Music;
